import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportThread } from '../../models/Support/ISupportThread';
import { ThreadStatus, ThreadType } from '../../models/Support/SupportEnums';
import { getApiErrorMessage } from '../../utils/Support/supportHelpers';
import {
	invalidateTicket,
	invalidateTicketLists,
	invalidateTicketThreads,
	SUPPORT_PATHS,
	SUPPORT_QUERY_KEYS,
} from './supportQueryHelpers';

type ThreadsQueryData = IPagedCollection<ISupportThread>;

interface AddThreadParams {
	type: ThreadType;
	body: string;
	attachmentIds?: string[];
	mentionedUserIds?: string[];
}

export function useAddThreadMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: AddThreadParams) =>
			userSession.webServiceHelper.callAsync<ISupportThread>(SUPPORT_PATHS.threads.list(ticketId), 'POST', params),
		onSuccess: () => invalidateTicketThreads(queryClient, ticketId),
	});
}

export function useUpdateThreadStatusMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const threadsKey = SUPPORT_QUERY_KEYS.tickets.threads(ticketId);

	return useMutation({
		mutationFn: ({ threadId, status }: { threadId: string; status: ThreadStatus | string }) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.status(ticketId, threadId), 'PUT', status),
		// Optimistic update: write the new status to the cached threads immediately so
		// the UI reflects Accept/Reject without waiting for the server round-trip or
		// the next poll. Stash previous data so onError can roll back.
		onMutate: async ({ threadId, status }) => {
			await queryClient.cancelQueries({ queryKey: threadsKey });
			const previous = queryClient.getQueryData<ThreadsQueryData>(threadsKey);
			if (previous) {
				queryClient.setQueryData<ThreadsQueryData>(threadsKey, {
					...previous,
					values: previous.values.map(t => (t.id === threadId ? { ...t, status: status as ThreadStatus } : t)),
				});
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData<ThreadsQueryData>(threadsKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: threadsKey });
		},
	});
}

/** Deletes a note the current user wrote. The server refuses anyone else's note and any non-note thread. */
export function useDeleteNoteMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const threadsKey = SUPPORT_QUERY_KEYS.tickets.threads(ticketId);

	return useMutation({
		mutationFn: (threadId: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.detail(ticketId, threadId), 'DELETE'),
		// Removed only once the server confirms, so a refused delete never hides a note that still exists.
		onSuccess: (_data, threadId) => {
			queryClient.setQueryData<ThreadsQueryData>(threadsKey, previous =>
				previous ? { ...previous, values: previous.values.filter(t => t.id !== threadId) } : previous
			);
			invalidateTicketThreads(queryClient, ticketId);
			// The server records a NoteDeleted event and lowers the ticket's threadCount.
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.events(ticketId) });
			invalidateTicketLists(queryClient);
		},
	});
}

interface EditNoteParams {
	threadId: string;
	body: string;
	/** Every teammate the edited note mentions; the server notifies only the ones the edit adds. */
	mentionedUserIds: string[];
}

/** Edits a note the current user wrote. The server refuses anyone else's note and any non-note thread. */
export function useEditNoteMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const threadsKey = SUPPORT_QUERY_KEYS.tickets.threads(ticketId);

	return useMutation({
		mutationFn: ({ threadId, body, mentionedUserIds }: EditNoteParams) =>
			userSession.webServiceHelper.callAsync<ISupportThread>(SUPPORT_PATHS.threads.detail(ticketId, threadId), 'PUT', {
				body,
				mentionedUserIds,
			}),
		// Written only once the server confirms, so a refused edit never shows text that was not saved.
		onSuccess: (saved, { threadId }) => {
			if (saved) {
				queryClient.setQueryData<ThreadsQueryData>(threadsKey, previous =>
					previous
						? { ...previous, values: previous.values.map(t => (t.id === threadId ? { ...t, ...saved } : t)) }
						: previous
				);
			}
			invalidateTicketThreads(queryClient, ticketId);
		},
	});
}

export function useDeepDiveMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: () => userSession.webServiceHelper.callAsync<string>(SUPPORT_PATHS.threads.deepDive(ticketId), 'POST'),
	});
}

export function useRefineTextMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ text, fullReview }: { text: string; fullReview: boolean }) =>
			userSession.webServiceHelper.callAsync<string>(SUPPORT_PATHS.threads.refine(ticketId), 'POST', {
				text,
				fullReview,
			}),
	});
}

interface SendThreadParams {
	threadId: string;
	/** Recipient override; the server falls back to the ticket's contact when omitted. */
	to?: string;
	cc?: string[];
	bcc?: string[];
	/** Send & Close passes false to skip the normal Active → Pending transition; the server defaults to true. */
	transitionToPending?: boolean;
}

export function useSendThreadMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ threadId, to, cc, bcc, transitionToPending }: SendThreadParams) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.send(ticketId, threadId), 'POST', {
				to,
				cc,
				bcc,
				transitionToPending,
			}),
		// Sending marks the thread Sent and moves an Active ticket to Pending.
		onSuccess: () => {
			invalidateTicketThreads(queryClient, ticketId);
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

interface ForwardThreadParams {
	threadId: string;
	to: string[];
	cc?: string[];
	bcc?: string[];
	/** Cover note; the API sends it above the forwarded message and keeps it as the Forward thread's body. */
	body?: string;
	attachmentIds?: string[];
}

export function useForwardThreadMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ threadId, to, cc, bcc, body, attachmentIds }: ForwardThreadParams) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.forward(ticketId, threadId), 'POST', {
				to,
				cc,
				bcc,
				body,
				attachmentIds,
			}),
		// The API owns LastMessageAt ordering; refresh the list after it records the successful forward.
		onSuccess: () => {
			invalidateTicketThreads(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

/** Headroom for the browser to start reading the archive before its object URL goes away. */
const REVOKE_OBJECT_URL_DELAY_MS = 1000;

interface DownloadThreadAttachmentsParams {
	threadId: string;
	/** Name for the saved archive — see the note on why the server's own name can't be used. */
	fileName: string;
}

/**
 * Downloads one message's attachments as a single zip.
 *
 * The endpoint answers with the archive bytes rather than an `OperationResult`, so the response is read
 * as a blob and handed to a synthetic anchor. The anchor has to carry the file name itself: the API is a
 * separate origin and exposes only `RequestId` to the browser, so its `Content-Disposition` name is not
 * readable here.
 */
export function useDownloadThreadAttachmentsMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ threadId, fileName }: DownloadThreadAttachmentsParams) =>
			new Promise<void>((resolve, reject) => {
				userSession.webServiceHelper
					.callWebServiceRawResponse(
						SUPPORT_PATHS.threads.attachmentsZip(ticketId, threadId),
						'GET',
						null,
						async response => {
							// WebServiceHelper invokes this callback without awaiting it, so a rejection escaping
							// here settles neither branch of the promise: the mutation stays pending, the control
							// spins for good, and no error toast ever fires.
							try {
								const blob = await response.blob();
								const objectUrl = window.URL.createObjectURL(blob);
								const anchor = document.createElement('a');
								anchor.href = objectUrl;
								anchor.download = fileName;
								// Safari only honors a click on an anchor that is attached to the document.
								document.body.appendChild(anchor);
								anchor.click();
								anchor.remove();
								// The click only schedules the download; the browser reads the object URL after
								// this tick, and revoking it too early cancels a large archive mid-flight.
								window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), REVOKE_OBJECT_URL_DELAY_MS);
								resolve();
							} catch (error) {
								reject(error);
							}
						},
						reject
					)
					.catch(reject);
			}),
		onError: (error: unknown) =>
			toast.error(getApiErrorMessage(error, 'Failed to download the attachments. Please try again.')),
	});
}
